import * as XLSX from 'xlsx';
import Papa from 'papaparse';

// SMS Lead Bot Phase 1 — client-side stock-file parsing.
//
// ⚠️ SHAPED LIKE bulkImportParse, NOT REUSED FROM IT. That module matches FIXED
// header labels and rejects a file that does not use them, which is right for a
// template we hand out and wrong for a file a client's DMS exports. Here the
// headers are unknown up front, so this returns the RAW headers and records and
// the mapping step decides what they mean.
//
// Everything below the mapping is server-side, exactly as bulk contact upload
// does it: this layer only reads the file.

export interface ParsedStockFile {
  headers: string[];
  records: Array<Record<string, unknown>>;
  /** Rows found, before mapping. Shown so an AM can sanity-check the parse. */
  rowCount: number;
}

function cellToString(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

export function parseStockCsvText(text: string): ParsedStockFile {
  const parsed = Papa.parse<Record<string, unknown>>(text, {
    header: true,
    skipEmptyLines: true,
  });
  const headers = (parsed.meta.fields ?? []).filter((h) => (h ?? '').trim() !== '');
  return { headers, records: parsed.data, rowCount: parsed.data.length };
}

function sheetToHeadersAndRecords(sheet: XLSX.WorkSheet): ParsedStockFile {
  const aoa = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    raw: true,
    blankrows: false,
  });
  if (aoa.length === 0) return { headers: [], records: [], rowCount: 0 };
  const headers = (aoa[0] ?? []).map((v) => cellToString(v)).filter((h) => h !== '');
  const rawHeaders = (aoa[0] ?? []).map((v) => cellToString(v));
  const records = aoa.slice(1).map((rowArr) => {
    const record: Record<string, unknown> = {};
    rawHeaders.forEach((header, i) => {
      if (header !== '') record[header] = (rowArr as unknown[])[i];
    });
    return record;
  });
  return { headers, records, rowCount: records.length };
}

export async function parseStockFile(file: File): Promise<ParsedStockFile> {
  if (/\.csv$/i.test(file.name)) return parseStockCsvText(await file.text());
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) return { headers: [], records: [], rowCount: 0 };
  return sheetToHeadersAndRecords(workbook.Sheets[sheetName]!);
}
